/**
 * PetTab — renders the Pet journal tab from a `JournalTabState`.
 *
 * Thin Phaser renderer mirroring `RelicsTab`. The snapshot → tab-state
 * decision lives in `journalDispatch.buildJournalTabState('pet', snapshot)`.
 * Pre-hatch: a single diegetic-cue line. Post-hatch: form heading +
 * concept, personality blurb, and active behavior milestones.
 *
 * Hard non-goals (Round 10): no pet customization UI, no bond display,
 * no inventory.
 */

import * as Phaser from 'phaser';
import {
  JOURNAL_FONT,
  JOURNAL_INK,
  JOURNAL_INK_MUTED,
  JOURNAL_FONT_SIZE,
  JOURNAL_LAYOUT,
} from './journalTheme';
import type { JournalTabState } from './journalDispatch';

/** Render the Pet tab into `container`. Container is cleared before
 *  the new lines are laid out. */
export function renderPetTab(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  state: JournalTabState,
): void {
  container.removeAll(true);
  if (state.lines.length === 0) {
    appendEmptyState(scene, container, state.emptyMessage);
    return;
  }

  let y = 0;
  for (const line of state.lines) {
    if (line.heading) {
      const heading = scene.add.text(0, y, line.heading, {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.HEADING,
        color: JOURNAL_INK,
        fontStyle: 'bold',
      });
      container.add(heading);
      y += 22;
    }

    const body = scene.add.text(0, y, line.body, {
      fontFamily: JOURNAL_FONT,
      fontSize: JOURNAL_FONT_SIZE.BODY,
      color: JOURNAL_INK_MUTED,
      fontStyle: 'italic',
      wordWrap: { width: JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2 },
    });
    container.add(body);
    y += body.height + 6;

    if (line.footnote) {
      const footnote = scene.add.text(0, y, line.footnote, {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.FOOTNOTE,
        color: JOURNAL_INK_MUTED,
      });
      container.add(footnote);
      y += 16;
    }
    y += JOURNAL_LAYOUT.LINE_SPACING;
  }
}

function appendEmptyState(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  message: string,
): void {
  const text = scene.add.text(0, 24, message, {
    fontFamily: JOURNAL_FONT,
    fontSize: JOURNAL_FONT_SIZE.EMPTY_STATE,
    color: JOURNAL_INK_MUTED,
    fontStyle: 'italic',
    wordWrap: { width: JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2 },
  });
  container.add(text);
}
